import React, { useContext, useEffect, useRef } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import classNames from 'classnames'
import { IonLifeCycleContext } from '@ionic/react'
import { TabBar, TabButton } from '@mytours/components'
import useTabBar from '../useTabBar'

export interface TabEvent {
	detail: { tab: string }
}

export interface HomeTab {
	tab: string
	href?: string
	className: string
	dataCy: string
	content: ReactNode
}

interface TabBarProps {
	tabs: HomeTab[]
	className?: string
	dir?: string
	onWillChange?: (event: TabEvent) => void
	onDidChange?: (event: TabEvent) => void
}

export const HomeTabBar: React.FC<TabBarProps> = ({ tabs, className, dir, onWillChange, onDidChange }) => {
	const { activeTab, hrefFor, select } = useTabBar(tabs, {
		onWillChange: tab => onWillChange?.({ detail: { tab } }),
		onDidChange: tab => onDidChange?.({ detail: { tab } }),
	})

	return (
		<TabBar className={className} dir={dir}>
			{tabs.map(({ tab, className: buttonClassName, dataCy, content }) => (
				<TabButton
					key={tab}
					tab={tab}
					href={hrefFor(tab)}
					selected={tab === activeTab}
					className={buttonClassName}
					data-cy={dataCy}
					onSelect={() => select(tab)}
				>
					{content}
				</TabButton>
			))}
		</TabBar>
	)
}

const HOST: CSSProperties = {
	display: 'flex',
	position: 'absolute',
	top: '0',
	left: '0',
	right: '0',
	bottom: '0',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	contain: 'layout size style',
}

const INNER: CSSProperties = { position: 'relative', flex: 1, contain: 'layout size style' }

const VIEW_EVENTS = ['ionViewWillEnter', 'ionViewDidEnter', 'ionViewWillLeave', 'ionViewDidLeave'] as const

interface TabsProps {
	className?: string
	outlet: ReactNode
	tabBar: ReactNode
}

// Ionic's tabs layout, the outlet over the bar, which also passes its pages' view events up to the lifecycle around it.
export const HomeTabs: React.FC<TabsProps> = ({ className, outlet, tabBar }) => {
	const ref = useRef<HTMLDivElement>(null)
	const lifeCycle = useContext(IonLifeCycleContext)

	useEffect(() => {
		const page = ref.current
		const handlers = VIEW_EVENTS.map(name => [name, () => lifeCycle[name]()] as const)
		handlers.forEach(([name, handler]) => page?.addEventListener(name, handler))
		return () => handlers.forEach(([name, handler]) => page?.removeEventListener(name, handler))
	}, [lifeCycle])

	return (
		<div ref={ref} className={classNames(className, 'ion-page')}>
			<div className="home-tabs-host" style={HOST}>
				<div className="tabs-inner" style={INNER}>
					{outlet}
				</div>
				{tabBar}
			</div>
		</div>
	)
}
